import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Flame, Coins, Loader2, Star, ArrowRight, AlertCircle, RefreshCw } from "lucide-react";
import api from "../lib/api";

const Row = ({ q, trend }) => {
  const color = trend === "up" ? "#5C8D73" : "#C66B59";
  const Icon = trend === "up" ? TrendingUp : TrendingDown;
  return (
    <Link
      to={`/asset/${q.asset_type}/${q.symbol}`}
      data-testid={`mover-${trend}-${q.symbol}`}
      className="group flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F9F8F6] transition-soft"
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: q.asset_type === "stock" ? "#547A8F15" : "#D18E6315" }}
      >
        {q.asset_type === "stock" ? (
          <TrendingUp className="w-4 h-4 text-[#547A8F]" strokeWidth={1.75} />
        ) : (
          <Coins className="w-4 h-4 text-[#D18E63]" strokeWidth={1.75} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono-ticker font-semibold text-sm text-[#2A2F2B]">{q.symbol}</div>
        <div className="text-xs text-[#6B7068] truncate">{q.name}</div>
      </div>
      <div className="text-right">
        <div className="font-mono-ticker text-sm text-[#2A2F2B]">
          ${q.price < 1 ? q.price.toFixed(4) : q.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className="flex items-center justify-end gap-1 text-xs font-mono-ticker font-semibold" style={{ color }}>
          <Icon className="w-3 h-3" strokeWidth={2.5} />
          {q.change_pct >= 0 ? "+" : ""}{q.change_pct.toFixed(2)}%
        </div>
      </div>
    </Link>
  );
};

export default function DailyMovers({ range = "1D", rangeLabel = "1D" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api.get(`/dashboard/movers?range=${encodeURIComponent(range)}`)
      .then((r) => setData(r.data))
      .catch(() => { setError(true); setData(null); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading daily movers...
      </div>
    );
  }

  // Network/server error
  if (error || !data) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 text-center" data-testid="movers-error">
        <div className="w-12 h-12 rounded-full bg-[#C66B59]/10 mx-auto flex items-center justify-center mb-3">
          <AlertCircle className="w-5 h-5 text-[#C66B59]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-lg text-[#2A2F2B] mb-1">Couldn't load movers</h3>
        <p className="text-sm text-[#6B7068] mb-4">We hit a snag reaching the market data service.</p>
        <button
          onClick={load}
          data-testid="movers-retry"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm transition-soft"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Retry
        </button>
      </div>
    );
  }

  // Empty watchlist
  if ((data.total_watched ?? 0) === 0) {
    return (
      <div
        className="rounded-2xl bg-white border border-[#EAE6E1] p-8 text-center"
        data-testid="movers-empty"
      >
        <div className="w-12 h-12 rounded-full bg-[#F9F8F6] mx-auto flex items-center justify-center mb-3">
          <Flame className="w-5 h-5 text-[#6B7068]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-lg text-[#2A2F2B] mb-1">No movers yet</h3>
        <p className="text-sm text-[#6B7068] mb-4">
          Add assets to your watchlist to see top gainers & losers here every day.
        </p>
        <Link
          to="/watchlist"
          data-testid="movers-empty-cta"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm transition-soft"
        >
          <Star className="w-4 h-4" strokeWidth={1.5} /> Build your watchlist
          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  // Watchlist has items but upstream failed to quote any
  if ((data.total_tracked ?? 0) === 0) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 text-center" data-testid="movers-upstream-fail">
        <div className="w-12 h-12 rounded-full bg-[#E8A25D]/15 mx-auto flex items-center justify-center mb-3">
          <AlertCircle className="w-5 h-5 text-[#E8A25D]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-lg text-[#2A2F2B] mb-1">Prices are catching up</h3>
        <p className="text-sm text-[#6B7068] mb-4">
          We're watching {data.total_watched} asset{data.total_watched === 1 ? "" : "s"} but the market data service is slow or rate-limited right now.
        </p>
        <button
          onClick={load}
          data-testid="movers-retry-soft"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EAE6E1] hover:border-[#547A8F] text-[#2A2F2B] text-sm transition-soft"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-testid="daily-movers">
      <div className="rounded-2xl bg-white border border-[#EAE6E1] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#EAE6E1] bg-[#5C8D73]/5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#5C8D73]/15 flex items-center justify-center">
              <TrendingUp className="w-3.5 h-3.5 text-[#5C8D73]" strokeWidth={2} />
            </div>
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2A2F2B]">
              Top gainers · {rangeLabel}
            </span>
          </div>
          <span className="text-xs font-mono-ticker text-[#6B7068]">
            {data.gainers.length}
          </span>
        </div>
        <div className="p-1">
          {data.gainers.length === 0 ? (
            <div className="px-4 py-6 text-xs text-[#6B7068] text-center" data-testid="no-gainers">
              No gainers in your watchlist today.
            </div>
          ) : (
            data.gainers.map((q) => <Row key={`g-${q.symbol}`} q={q} trend="up" />)
          )}
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-[#EAE6E1] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#EAE6E1] bg-[#C66B59]/5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#C66B59]/15 flex items-center justify-center">
              <TrendingDown className="w-3.5 h-3.5 text-[#C66B59]" strokeWidth={2} />
            </div>
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2A2F2B]">
              Top losers · {rangeLabel}
            </span>
          </div>
          <span className="text-xs font-mono-ticker text-[#6B7068]">
            {data.losers.length}
          </span>
        </div>
        <div className="p-1">
          {data.losers.length === 0 ? (
            <div className="px-4 py-6 text-xs text-[#6B7068] text-center" data-testid="no-losers">
              No losers in your watchlist today.
            </div>
          ) : (
            data.losers.map((q) => <Row key={`l-${q.symbol}`} q={q} trend="down" />)
          )}
        </div>
      </div>
    </div>
  );
}
